"use client"

import React, { createContext, useContext, ReactNode, useCallback, useMemo } from 'react'

export interface ApiConfig {
    baseUrl: string
    services: Record<string, string>
    serviceDomains?: Record<string, string>
    resolve: (params: { url: string; service: string }) => string
    defaultOrganization?: string
    ignoreStatuses?: number[]
    requireAuth?: boolean
    authToken?: string | null
    tokenRefreshServices?: string[]
    onTokenRefresh?: () => Promise<string | null>
    onUnauthorized?: (params: { service: string; url: string }) => void
    skipOrganizationHeaderServices?: string[]
    corsRestrictedServices?: string[]
}

export interface ApiContextValue {
    config: ApiConfig
    updateConfig: (newConfig: Partial<ApiConfig>) => void
}

const ApiContext = createContext<ApiContextValue | null>(null)

export interface ApiProviderProps {
    children: ReactNode
    config: ApiConfig
}

export function ApiProvider({ children, config }: ApiProviderProps) {
    const [apiConfig, setApiConfig] = React.useState<ApiConfig>(config)

    const updateConfig = useCallback((newConfig: Partial<ApiConfig>) => {
        setApiConfig(prev => ({ ...prev, ...newConfig }))
    }, [])

    const contextValue = useMemo<ApiContextValue>(() => ({
        config: apiConfig,
        updateConfig
    }), [apiConfig, updateConfig])

    return (
        <ApiContext.Provider value={contextValue}>
            {children}
        </ApiContext.Provider>
    )
}

export function useApiContext(): ApiContextValue {
    const context = useContext(ApiContext)
    if (!context) {
        throw new Error('useApiContext must be used within an ApiProvider')
    }
    return context
}
